import React, { useState, useEffect } from 'react';
import {
  Clock,
  Users,
  ChevronRight,
  MapPin,
  AlertCircle,
  CheckCircle,
  ClipboardList,
  Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Loading } from '../../components/ui/Loading';

interface ScheduledClass {
  id: string;
  name: string;
  date: string;
  time: string;
  endTime?: string;
  enrolled: Record<string, any>;
  maxCapacity: number;
  location?: string;
  attendance?: Record<string, boolean>;
}

const InstructorAttendance: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [todayClasses, setTodayClasses] = useState<ScheduledClass[]>([]);
  const [recentClasses, setRecentClasses] = useState<ScheduledClass[]>([]);
  const [instructorId, setInstructorId] = useState<string | null>(null);

  useEffect(() => {
    if (businessId && currentUser) {
      loadInstructorId();
    }
  }, [businessId, currentUser]);

  useEffect(() => {
    if (instructorId) {
      loadClasses();
    }
  }, [instructorId]);

  const loadInstructorId = async () => {
    if (!businessId || !currentUser) return;

    try {
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('userId', '==', currentUser.id)
      );
      const snapshot = await getDocs(instructorsQuery);

      if (!snapshot.empty) {
        setInstructorId(snapshot.docs[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error cargando instructor:', error);
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    if (!businessId || !instructorId) return;

    try {
      setLoading(true);

      // Formato local YYYY-MM-DD sin conversión a UTC
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('instructorId', '==', instructorId),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(classesQuery);

      const allClasses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ScheduledClass[];

      // Clases de hoy
      const todaysClasses = allClasses
        .filter(c => c.date === today)
        .sort((a, b) => a.time.localeCompare(b.time));
      setTodayClasses(todaysClasses);

      // Clases recientes (últimos 3 días) sin asistencia completada
      const recent = allClasses
        .filter(c => {
          if (c.date >= today) return false;
          const classDate = new Date(c.date + 'T00:00:00');
          return classDate >= threeDaysAgo;
        })
        .filter(c => {
          // Mostrar si no tiene asistencia o está incompleta
          const enrolledCount = Object.keys(c.enrolled || {}).length;
          const attendanceCount = Object.keys(c.attendance || {}).length;
          return attendanceCount < enrolledCount;
        })
        .sort((a, b) => {
          if (a.date === b.date) return b.time.localeCompare(a.time);
          return b.date.localeCompare(a.date);
        });
      setRecentClasses(recent);

    } catch (error) {
      console.error('Error cargando clases:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => time.substring(0, 5);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const getAttendanceStatus = (classItem: ScheduledClass) => {
    const enrolledCount = Object.keys(classItem.enrolled || {}).length;
    const attendanceCount = Object.keys(classItem.attendance || {}).length;
    
    if (attendanceCount === 0) return 'pending';
    if (attendanceCount < enrolledCount) return 'partial';
    return 'complete';
  };

  if (!instructorId && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Cuenta no vinculada</h2>
          <p className="text-gray-400">
            Tu cuenta no está vinculada a un perfil de instructor.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Pasar Lista</h1>
        <p className="text-gray-400">Selecciona una clase para registrar asistencia</p>
      </div>

      {/* Clases de hoy */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-primary-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Clases de Hoy</h2>
        </div>

        {todayClasses.length === 0 ? (
          <Card className="p-8 text-center">
            <ClipboardList className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No tienes clases programadas para hoy</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {todayClasses.map((classItem) => {
              const enrolledCount = Object.keys(classItem.enrolled || {}).length;
              const status = getAttendanceStatus(classItem);
              const presentCount = Object.values(classItem.attendance || {}).filter(Boolean).length;

              return (
                <Card
                  key={classItem.id}
                  className={`p-4 cursor-pointer transition-all hover:border-primary-500/50 ${
                    status === 'complete' ? 'border-green-500/30 bg-green-500/5' : ''
                  }`}
                  onClick={() => navigate(`/instructor/class/${classItem.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        status === 'complete' 
                          ? 'bg-green-500/20' 
                          : status === 'partial'
                            ? 'bg-amber-500/20'
                            : 'bg-gray-700'
                      }`}>
                        {status === 'complete' ? (
                          <CheckCircle className="w-6 h-6 text-green-400" />
                        ) : (
                          <ClipboardList className={`w-6 h-6 ${
                            status === 'partial' ? 'text-amber-400' : 'text-gray-400'
                          }`} />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{classItem.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(classItem.time)}
                          </span>
                          {classItem.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {classItem.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-sm">
                          <Users className="w-4 h-4 text-gray-500" />
                          <span className={`font-medium ${
                            status === 'complete' ? 'text-green-400' : 'text-gray-300'
                          }`}>
                            {status === 'complete' || status === 'partial'
                              ? `${presentCount}/${enrolledCount}`
                              : enrolledCount
                            }
                          </span>
                        </div>
                        <span className={`text-xs ${
                          status === 'complete' 
                            ? 'text-green-400' 
                            : status === 'partial'
                              ? 'text-amber-400'
                              : 'text-gray-500'
                        }`}>
                          {status === 'complete' 
                            ? 'Completado' 
                            : status === 'partial'
                              ? 'Parcial'
                              : 'Pendiente'
                          }
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Clases pendientes de asistencia */}
      {recentClasses.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-amber-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Pendientes de Asistencia</h2>
          </div>
          <p className="text-sm text-gray-400 mb-3">
            Clases recientes que no tienen la asistencia completa
          </p>
          <div className="space-y-2">
            {recentClasses.map((classItem) => {
              const enrolledCount = Object.keys(classItem.enrolled || {}).length;
              const attendanceCount = Object.keys(classItem.attendance || {}).length;

              return (
                <Card
                  key={classItem.id}
                  className="p-3 cursor-pointer transition-all hover:border-amber-500/50 border-amber-500/20 bg-amber-500/5"
                  onClick={() => navigate(`/instructor/class/${classItem.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">{classItem.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <span>{formatDate(classItem.date)}</span>
                        <span>•</span>
                        <span>{formatTime(classItem.time)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-amber-400">
                        {attendanceCount}/{enrolledCount} registrados
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Mensaje si todo está al día */}
      {todayClasses.length === 0 && recentClasses.length === 0 && (
        <Card className="p-8 text-center bg-green-500/10 border-green-500/30">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-white mb-2">¡Todo al día!</h3>
          <p className="text-gray-400">
            No tienes clases pendientes de registrar asistencia
          </p>
        </Card>
      )}
    </div>
  );
};

export default InstructorAttendance;










